// Shared billing bits (PLAN-offers.md §15): an offer's price and usage lines, its usage tiers, the subscription
// badge, the pool meter, the low-usage-per-user warning, the VAT of an order or a quote (PLAN-payments.md §4).
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { PROFILE_ANCHOR } from './billingProfile';
import { date, money } from './format';
import { Badge } from './ui';
import type { CreditTier, Offer, SubscriptionBrief, SubscriptionStatus, VatFields } from '../types';

type T = (k: string, o?: Record<string, unknown>) => string;

/** The offer every new organization starts on; it cannot be chosen (the API answers `free_not_selectable`). */
export const FREE_OFFER = 'free';

/** Below this much usage per user per month (in the offer's currency), a Team's members risk being blocked early in
 *  the month. */
export const LOW_USAGE_PER_USER = 30;

export function statusTone(status: SubscriptionStatus): 'accent' | 'neutral' | 'warn' | 'danger' {
  return status === 'active' ? 'accent' : status === 'trialing' ? 'neutral' : status === 'past_due' ? 'danger' : 'warn';
}

export function SubscriptionBadge({ sub }: { sub: SubscriptionBrief | null }) {
  const { t } = useTranslation();
  if (!sub) return <Badge tone="warn">{t('billing.none')}</Badge>;
  return (
    <Badge tone={statusTone(sub.status)}>
      {sub.offer_name} · {t(`billing.status.${sub.status}`)}
    </Badge>
  );
}

export function tiersOf(offer: Offer): CreditTier[] {
  return offer.terms.credit_tiers ?? [];
}

/** "€600 of credits (+20 %)" — the bonus only when the tier gives more than it costs. */
export function tierUsage(tier: CreditTier, currency: string, t: T, locale: string): string {
  const bonus = Math.round((Number(tier.usage) / Number(tier.price) - 1) * 100);
  const usage = t('pricing.tierUsage', { usage: money(tier.usage, currency, locale) });
  return bonus > 0 ? `${usage} (+${bonus} %)` : usage;
}

/** Tier price + users × price per user, excluding VAT. */
export function monthlyTotal(offer: Offer, tierPrice: string | null, seats: number): number {
  return Number(tierPrice ?? 0) + Number(offer.terms.price_per_seat) * seats;
}

/** Team-like offers: several users draw on one pool of credits, chosen as a tier, with each seat billed on top. */
export function sharedCredits(offer: Offer): boolean {
  return tiersOf(offer).length > 0 && offer.terms.max_seats !== 1;
}

/** A tier's credits divided among the users (the figure the low-usage warning checks). */
export function usagePerUser(tier: CreditTier, seats: number): number {
  return Number(tier.usage) / Math.max(seats, 1);
}

/** The price in its parts: the seats (users × price per user), the credit tier, the total, and the credits each user
 *  gets on average when the tier is shared. Excluding VAT. */
export interface PriceParts {
  seats: number;
  seatPrice: number;
  seatsTotal: number;
  tier: CreditTier | null;
  shared: boolean;
  total: number;
  perUser: number | null;
}

export function priceParts(offer: Offer, tierPrice: string | null, seats: number): PriceParts {
  const tier = tiersOf(offer).find((x) => Number(x.price) === Number(tierPrice)) ?? null;
  const seatPrice = Number(offer.terms.price_per_seat);
  const shared = sharedCredits(offer);
  return {
    seats,
    seatPrice,
    seatsTotal: seatPrice * seats,
    tier,
    shared,
    total: monthlyTotal(offer, tier?.price ?? null, seats),
    perUser: shared && tier ? usagePerUser(tier, seats) : null,
  };
}

/** The price lines of an offer card: "€50.00 per user / month" then "+ shared monthly credits from €50.00" (Team),
 *  "From €30.00 / month" (Solo), "€29.00 per user / month", "Free", "Free for 14 days". */
export function offerPrice(offer: Offer, t: T, locale: string): string[] {
  const v = offer.terms;
  if (offer.trial) return [t('pricing.trialFor', { days: v.trial_days })];
  const tiers = tiersOf(offer);
  const seat = money(v.price_per_seat, v.currency, locale);
  if (tiers.length) {
    const from = money(tiers[0].price, v.currency, locale);
    if (Number(v.price_per_seat) <= 0) return [t('pricing.fromTier', { price: from })];
    return [t('pricing.perSeat', { price: seat }), t(sharedCredits(offer) ? 'pricing.plusSharedCredits' : 'pricing.plusCredits', { price: from })];
  }
  return [Number(v.price_per_seat) > 0 ? t('pricing.perSeat', { price: seat }) : t('billing.free')];
}

/** The price parts as rows: seats, credits (with the usage they give), total excl. VAT, and the usage per user. */
export function PriceBreakdown({ offer, tierPrice, seats, vat = null }: { offer: Offer; tierPrice: string | null; seats: number; vat?: VatFields | null }) {
  const { t, i18n } = useTranslation();
  const l = i18n.language;
  const cur = offer.terms.currency;
  const p = priceParts(offer, tierPrice, seats);
  // The month's VAT once the scheme is known (not before the billing profile, not while held for review), rounded as the API does.
  const scheme = vat && vat.vat_rate !== null && vat.vat_scheme !== null && vat.vat_scheme !== 'review' ? vat.vat_scheme : null;
  const vatAmount = scheme ? Math.round(p.total * Number(vat!.vat_rate) * 100) / 100 : 0;
  const vatLabel = scheme === 'reverse_charge' ? t('billing.vat.reverseCharge') : scheme === 'outside_scope' ? t('billing.vat.outsideScope') : t('checkout.vatLine', { rate: vatRate(vat?.vat_rate ?? '0', l) });
  const row = (label: string, value: string, detail?: string, testId?: string) => (
    <div className="flex justify-between gap-3" data-testid={testId}>
      <span className="text-muted">{label}</span>
      {detail && <span className="ml-auto font-mono text-faint">{detail}</span>}
      <span className="font-mono text-ink">{value}</span>
    </div>
  );
  return (
    <div className="flex flex-col gap-1 text-[13px]" data-testid="price-breakdown">
      {p.seatPrice > 0 && row(t('pricing.breakdown.seats'), money(p.seatsTotal, cur, l), `${p.seats} × ${money(p.seatPrice, cur, l)}`, 'breakdown-seats')}
      {p.tier && (
        <>
          {row(t(p.shared ? 'pricing.breakdown.sharedCredits' : 'pricing.breakdown.credits'), money(p.tier.price, cur, l), undefined, 'breakdown-credits')}
          <span className="text-[12px] text-faint">
            {t(p.shared ? 'pricing.breakdown.usageTeam' : 'pricing.breakdown.usage', { usage: money(p.tier.usage, cur, l) })}
          </span>
        </>
      )}
      <div className="mt-1 flex justify-between gap-3 border-t border-line-row pt-1.5 font-medium text-ink" data-testid="breakdown-total">
        <span>{t('pricing.breakdown.total')}</span>
        <span className="font-mono">{money(p.total, cur, l)}</span>
      </div>
      {scheme && (
        <>
          {row(vatLabel, money(vatAmount, cur, l), undefined, 'breakdown-vat')}
          <div className="flex justify-between gap-3 font-medium text-ink" data-testid="breakdown-total-incl">
            <span>{t('pricing.breakdown.totalInclVat')}</span>
            <span className="font-mono">{money(p.total + vatAmount, cur, l)}</span>
          </div>
        </>
      )}
      {p.perUser !== null && (
        <span className="text-[12px] text-muted" data-testid="breakdown-per-user">
          {t('pricing.breakdown.perUser', { perUser: money(p.perUser, cur, l) })}
        </span>
      )}
    </div>
  );
}

/** The usage lines of an offer: included usage (or its tiers' range), the cap per member, the number of users. */
export function offerFacts(offer: Offer, t: T, locale: string): string[] {
  const v = offer.terms;
  const tiers = tiersOf(offer);
  const facts: string[] = [];
  if (tiers.length) {
    facts.push(t(sharedCredits(offer) ? 'pricing.tierRangeShared' : 'pricing.tierRange', { min: money(tiers[0].usage, v.currency, locale), max: money(tiers[tiers.length - 1].usage, v.currency, locale) }));
  } else if (v.allowance_per_seat_eur === null) {
    facts.push(t('pricing.unlimitedUsage'));
  } else if (offer.trial) {
    facts.push(t('pricing.trialUsage', { amount: money(v.allowance_per_seat_eur, v.currency, locale) }));
  } else {
    facts.push(t(v.max_seats === 1 ? 'pricing.usagePerMonth' : 'pricing.usagePerSeat', { amount: money(v.allowance_per_seat_eur, v.currency, locale) }));
  }
  if (v.user_cap_eur !== null) facts.push(t('pricing.userCap', { amount: money(v.user_cap_eur, v.currency, locale) }));
  if (v.max_seats === 1) facts.push(t('pricing.oneUser'));
  else facts.push(v.max_seats === null ? (v.min_seats > 1 ? t('pricing.seatsFrom', { min: v.min_seats }) : t('pricing.anyUsers')) : t('pricing.seatsRange', { min: v.min_seats, max: v.max_seats }));
  return facts;
}

/** The offer's selling points: translated for the launch offers, else the lines staff wrote in the backoffice. */
export function offerFeatures(offer: Offer, t: T): string[] {
  const key = `pricing.features.${offer.key}`;
  const translated = (t as unknown as (k: string, o: { returnObjects: true }) => unknown)(key, { returnObjects: true });
  return Array.isArray(translated) ? (translated as string[]) : offer.terms.features;
}

/** The chosen tier's usage per user, when it is under the advised minimum (Team); null otherwise. */
export function lowUsageWarning(offer: Offer, tierPrice: string | null, seats: number, t: T, locale: string): string | null {
  if (Number(offer.terms.price_per_seat) <= 0 || seats < 1) return null;
  const tier = tiersOf(offer).find((x) => Number(x.price) === Number(tierPrice));
  if (!tier) return null;
  const perUser = usagePerUser(tier, seats);
  if (perUser >= LOW_USAGE_PER_USER) return null;
  return t('pricing.lowUsage', { perUser: money(perUser, offer.terms.currency, locale), min: money(LOW_USAGE_PER_USER, offer.terms.currency, locale) });
}

/** Spent / pool with a bar; `pool` null = unlimited. */
/** Why this period's monthly credits are below the offer's: a change paid mid-period added them for the days left. */
export function ProratedNote({ since, full, periodEnd, currency, locale }: { since: string; full: number; periodEnd: string | null; currency: string; locale: string }) {
  const { t } = useTranslation();
  return (
    <p className="text-[12px] text-muted" data-testid="prorated-note">
      {t('billing.prorated', { date: date(since, locale), full: money(full, currency, locale) })}
      {periodEnd ? ` ${t('billing.proratedNext', { full: money(full, currency, locale), date: date(periodEnd, locale) })}` : ''}
    </p>
  );
}

export function PoolMeter({ spent, pool, currency, locale, prorated }: { spent: number; pool: number | null; currency: string; locale: string;
  prorated?: { since: string; full: number; periodEnd: string | null } }) {
  const { t } = useTranslation();
  const ratio = pool ? Math.min(1, Math.max(0, spent / pool)) : 0;
  return (
    <div className="flex flex-col gap-1.5" data-testid="pool-meter">
      <div className="flex flex-wrap items-baseline justify-between gap-2 text-[13px]">
        <span className="text-muted">{t('billing.poolLabel')}</span>
        <span className="font-mono text-ink">
          {pool === null ? t('billing.poolUnlimited', { spent: money(spent, currency, locale) }) : t('billing.poolUsed', { spent: money(spent, currency, locale), pool: money(pool, currency, locale) })}
        </span>
      </div>
      {pool !== null && (
        <div className="h-2 w-full overflow-hidden rounded-full bg-hover" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(ratio * 100)}>
          <div className={`h-full rounded-full ${ratio >= 1 ? 'bg-danger' : ratio > 0.8 ? 'bg-warn' : 'bg-accent'}`} style={{ width: `${Math.round(ratio * 100)}%` }} />
        </div>
      )}
      {prorated && <ProratedNote {...prorated} currency={currency} locale={locale} />}
    </div>
  );
}

/** "20 %" in French, "20%" in English, "8,5 %" for the overseas departments. */
export function vatRate(rate: string, locale: string): string {
  return new Intl.NumberFormat(locale === 'fr' ? 'fr-FR' : 'en-US', { style: 'percent', maximumFractionDigits: 1 }).format(Number(rate));
}

/** The VAT line of an order or a quote and its total incl. VAT; or why there is none yet: the VAT number is being
 *  verified (the order is held), or the billing profile is not filled (a link for admins). */
export function VatBreakdown({ vat, currency, admin }: { vat: VatFields; currency: string; admin: boolean }) {
  const { t, i18n } = useTranslation();
  const l = i18n.language;
  if (vat.vat_scheme === 'review') return <span data-testid="vat">{t('billing.vat.held')}</span>;
  if (vat.vat_scheme === null || vat.total === null) {
    return (
      <span data-testid="vat">
        {t('billing.vat.noProfile')}
        {admin && (
          <>
            {' '}
            <Link to={`/organization#${PROFILE_ANCHOR}`} className="text-accent underline">
              {t('billing.vat.noProfileLink')}
            </Link>
          </>
        )}
      </span>
    );
  }
  const line =
    vat.vat_scheme === 'reverse_charge'
      ? t('billing.vat.reverseCharge')
      : vat.vat_scheme === 'outside_scope'
        ? t('billing.vat.outsideScope')
        : t('billing.vat.line', { rate: vatRate(vat.vat_rate ?? '0', l), amount: money(vat.vat_amount ?? '0', currency, l) });
  return (
    <span data-testid="vat">
      {line} · <strong className="font-medium text-ink">{t('billing.vat.total', { total: money(vat.total, currency, l) })}</strong>
    </span>
  );
}
